// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

export const SelfServiceView = ({ user = null, onNavigateToRequests = undefined }: { user?: UserRow | null; onNavigateToRequests?: () => void }) => {
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState("Thinking...");
  const [templateSelected, setTemplateSelected] = useState<any>(null);
  const [requestCreated, setRequestCreated] = useState<any>(null);
  const [form, setForm] = useState<Record<string, any>>({ counterparty: "", business_unit: "", description: "" });
  const [creating, setCreating] = useState(false);
  const [chatBusinessUnit, setChatBusinessUnit] = useState<any>(null);
  const [ssBusinessUnits, setSsBusinessUnits] = useState<any[]>([]);
  useEffect(() => { apiCall('/settings/business-units').then(r => { if (r?.businessUnits) setSsBusinessUnits(r.businessUnits); }); }, []);
  // Template-derived starter prompts (owner ruling: generated from the tenant's
  // real generic templates, never hardcoded). null = still loading; [] = the
  // tenant has zero generic templates → fall back to the two-option panel.
  const [starters, setStarters] = useState<any[] | null>(null);
  useEffect(() => { apiCall('/self-service/starter-templates').then(r => setStarters(r?.starters || [])).catch(() => setStarters([])); }, []);
  // Framework-agreement deal details (opportunity / value / timeline) captured
  // by the chat; written into the description as a labeled block.
  const [dealDetails, setDealDetails] = useState<any>(null);
  const [cpValidation, setCpValidation] = useState<any>(null); // { legal_name, address, confidence }
  const [cpValidating, setCpValidating] = useState(false);
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [dupeChecked, setDupeChecked] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const messagesEndRef = useRef<any>(null);
  const cpDebounce = useRef<any>(null);
  // Legal Intake Expansion (owner ruling 4): the portal offers the question
  // kind too. Compact self-contained form; portal = internal form minus the
  // client entity (and, in Phase A, minus the existing-agreement picker — the
  // interpretation path stays available via internal intake). Posts to the
  // same /self-service/create-request endpoint with request_kind='question'.
  const [showQuestion, setShowQuestion] = useState(false);
  const [qForm, setQForm] = useState<Record<string, any>>({ title: "", matter_type: "", description: "", business_unit: "", priority: "Normal", targetDate: "" });
  const [qFile, setQFile] = useState<File | null>(null);
  const [qCreating, setQCreating] = useState(false);
  // Ask-Legal AI answer view: question box → grounded facts-only AI answer
  // (persistent AI-generated notice) → one-click "Send to counsel for review"
  // which prefills the EXISTING question form (same create-request path, same
  // badged queue) with the question + the AI answer for the seeded message.
  const [showAsk, setShowAsk] = useState(false);
  const [askQuestion, setAskQuestion] = useState("");
  const [askAnswer, setAskAnswer] = useState<any>(null); // { answer, interpretive, question }
  const [askLoading, setAskLoading] = useState(false);
  // No-template-match fallback: instead of a dead-end, offer two live paths
  // that carry the chat context forward. { title } once offered; cpName holds
  // the counterparty for the "Request without Document" path.
  const [noTemplateOffer, setNoTemplateOffer] = useState(false);
  const [fallbackCp, setFallbackCp] = useState("");
  const [fallbackCreating, setFallbackCreating] = useState(false);

  const chatSummaryText = () => messages.map((m: any) => `${m.role === "user" ? "User" : "AI"}: ${m.text}`).join("\n\n");
  const firstUserText = () => (messages.find((m: any) => m.role === "user")?.text || "").slice(0, 120);

  const runAskLegal = async (question: string) => {
    if (!question.trim()) return null;
    setAskLoading(true);
    try {
      const data = await apiCall("/self-service/ask-legal/answer", {
        method: "POST",
        body: JSON.stringify({ question: question.trim() }),
      });
      if (data?.answer) {
        const r = { answer: data.answer, interpretive: !!data.interpretive, question: question.trim() };
        setAskAnswer(r);
        return r;
      }
    } catch (err) { console.error("Ask-legal answer error:", err); }
    finally { setAskLoading(false); }
    setAskAnswer({ answer: "Sorry, the AI service could not produce an answer right now. You can still send your question to counsel using the button below.", interpretive: true, question: question.trim() });
    return null;
  };

  // One-click counsel hop: prefill the EXISTING question form with the
  // question + the AI answer so both land in the seeded inquiry message.
  const sendToCounsel = (question: string, answer: string | null) => {
    setQForm((p: any) => ({
      ...p,
      title: question.slice(0, 120) || "Legal question",
      description: answer
        ? `${question}\n\n--- AI-generated answer (for counsel review — not legal advice) ---\n\n${answer}`
        : question,
    }));
    setShowAsk(false);
    setShowQuestion(true);
  };
  const submitPortalQuestion = async () => {
    if (!qForm.title?.trim() || !qForm.matter_type || !qForm.description?.trim()) return;
    setQCreating(true);
    try {
      const chosenBu = (qForm.business_unit && String(qForm.business_unit).trim()) ? qForm.business_unit : null;
      const data = await apiCall("/self-service/create-request", {
        method: "POST",
        body: JSON.stringify({
          request_kind: "question",
          title: qForm.title.trim(),
          description: qForm.description.trim(),
          matter_type: String(qForm.matter_type).toLowerCase(),
          business_unit: chosenBu,
          priority: (qForm.priority || "Normal").toLowerCase(),
          target_date: qForm.targetDate || null,
        }),
      });
      if (data?.request) {
        // Optional attachment → the seeded inquiry message (thread-attachment
        // machinery), never a document version.
        if (qFile && data.request.question_message_id) {
          const token = localStorage.getItem("kontractai_token");
          const fd = new FormData();
          fd.append("attachments", qFile);
          try {
            await fetch(`/api/messages/${data.request.question_message_id}/attachments`, {
              method: "POST", headers: { "Authorization": `Bearer ${token}` }, body: fd,
            });
          } catch (err) { console.error("Portal question attachment upload failed:", err); }
        }
        setShowQuestion(false); setQFile(null); setRequestCreated({ ...data.request, escalated: true });
      }
      else { alert("Failed to submit question"); }
    } catch (_) { alert("Failed to submit question"); }
    setQCreating(false);
  };

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading, suggestions]);

  // Auto-populate business unit from chat AI selection
  useEffect(() => {
    if (chatBusinessUnit) setForm((p: any) => ({ ...p, business_unit: chatBusinessUnit }));
  }, [chatBusinessUnit]);

  // Validate counterparty when user stops typing
  useEffect(() => {
    if (!form.counterparty || form.counterparty.trim().length < 3) { setCpValidation(null); setDuplicates([]); setDupeChecked(false); return; }
    clearTimeout(cpDebounce.current);
    cpDebounce.current = setTimeout(async () => {
      // Run duplicate check and counterparty validation in parallel
      setCpValidating(true);
      try {
        const [cpResult, dupeResult] = await Promise.all([
          apiCall("/self-service/validate-counterparty", { method: "POST", body: JSON.stringify({ name: form.counterparty.trim() }) }).catch((): any => null),
          apiCall("/self-service/check-duplicates", { method: "POST", body: JSON.stringify({ counterparty: form.counterparty.trim(), agreement_type: templateSelected?.agreement_type || null }) }).catch((): any => null)
        ]);
        if (cpResult) setCpValidation(cpResult);
        if (dupeResult?.duplicates) { setDuplicates(dupeResult.duplicates); setDupeChecked(true); }
      } finally {
        setCpValidating(false);
      }
    }, 800);
    return () => clearTimeout(cpDebounce.current);
  }, [form.counterparty, templateSelected]);

  const sendMessage = async (overrideText: string | undefined = undefined) => {
    const text = overrideText || input.trim();
    if (!text || loading) return;
    const userMsg = { role: "user", text };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput("");
    setSuggestions([]);
    setShowOtherInput(false);
    setNoTemplateOffer(false);
    setLoading(true);
    try {
      const data = await apiCall("/self-service/chat", {
        method: "POST",
        body: JSON.stringify({ messages: updated.map(m => ({ role: m.role, content: m.text })) })
      });
      if (data?.response) {
        setMessages(prev => [...prev, { role: "assistant", text: data.response }]);
        if (data.template_selected) setTemplateSelected(data.template_selected);
        if (data.business_unit) setChatBusinessUnit(data.business_unit);
        if (data.deal_details) setDealDetails(data.deal_details);
        setSuggestions(data.suggestions || []);
        // Adaptive opening: a general question routes into the Ask-Legal
        // answer path — answer it right here in the chat, with the same
        // persistent AI notice + counsel hop the Ask modal carries.
        if (data.intent === "general_question") {
          setLoadingStatus("Checking the contract records...");
          setLoading(true);
          const r = await runAskLegal(text);
          setMessages(prev => [...prev, {
            role: "assistant",
            text: r?.answer || "Sorry, the AI service could not produce an answer right now. You can still send your question to counsel below.",
            aiAnswer: true,
            question: text,
          }]);
          setLoadingStatus("Thinking...");
        }
        // No fitting template: offer the two live paths instead of a dead-end.
        if (data.no_template_match) setNoTemplateOffer(true);
      } else {
        setMessages(prev => [...prev, {
          role: "assistant",
          text: "Sorry, I didn't get a response. The AI service may be temporarily busy — please try again in a moment."
        }]);
      }
    } catch (err: any) {
      console.error("Self-service chat error:", err);
      setMessages(prev => [...prev, {
        role: "assistant",
        text: "Network error. Please check your connection and try again."
      }]);
    }
    setLoading(false);
  };

  const createRequest = async () => {
    if (!form.counterparty) return;
    setCreating(true);
    try {
      const chatSummary = messages.map((m: any) => `${m.role === "user" ? "User" : "AI"}: ${m.text}`).join("\n\n");
      const cpName = cpValidation?.legal_name || form.counterparty;
      const cpAddr = cpValidation?.address || null;
      const autoTitle = templateSelected?.agreement_type || templateSelected?.name || 'Contract Request';
      const finalDescription = form.description?.trim() || ("Self-service request via AI questionnaire.\n\n" + chatSummary);
      // Framework-agreement details as a labeled, readable block ABOVE the raw
      // transcript (owner ruling: not buried in the transcript).
      const dealBlock = dealDetails ? `\n\n--- Opportunity / Value / Timeline ---\nOpportunity: ${dealDetails.opportunity || "Not provided"}\nPotential value: ${dealDetails.value || "Not provided"}\nTimeline: ${dealDetails.timeline || "Not provided"}` : "";
      console.log("[SelfService] Creating request:", { autoTitle, cpName, templateId: templateSelected?.id });
      const data = await apiCall("/self-service/create-request", {
        method: "POST",
        body: JSON.stringify({
          template_id: templateSelected?.id,
          title: autoTitle,
          counterparty: cpName,
          counterparty_address: cpAddr,
          counterparty_entity_type: cpValidation?.entity_type || null,
          counterparty_jurisdiction: cpValidation?.jurisdiction || null,
          counterparty_entity_designation: cpValidation?.entity_designation || null,
          description: `${finalDescription}${dealBlock}\n\n--- Self-Service Questionnaire Summary ---\n\n${chatSummary}${cpAddr ? `\n\nCounterparty Address: ${cpAddr}` : ""}${cpValidation?.entity_designation ? `\nEntity Type: ${cpValidation.entity_designation}` : ""}`,
          business_unit: form.business_unit || null
        })
      });
      console.log("[SelfService] Create request response:", data);
      if (data?.request) setRequestCreated({ ...data.request, versionCreated: data.version_created });
      else alert("Failed to create request");
    } catch (err: any) {
      console.error("[SelfService] Create request error:", err);
      alert("Error creating request: " + (err.message || "Unknown error"));
    }
    setCreating(false);
  };

  // Escalate directly — create request and immediately escalate
  const escalateToQueue = async () => {
    if (!form.counterparty) return;
    setEscalating(true);
    const chatSummary = messages.map((m: any) => `${m.role === "user" ? "User" : "AI"}: ${m.text}`).join("\n\n");
    const cpName = cpValidation?.legal_name || form.counterparty;
    const cpAddr = cpValidation?.address || null;
    const data = await apiCall("/self-service/create-request", {
      method: "POST",
      body: JSON.stringify({
        template_id: templateSelected?.id,
        title: form.title || templateSelected?.name || "Contract Request",
        counterparty: cpName,
        counterparty_address: cpAddr,
        counterparty_entity_type: cpValidation?.entity_type || null,
        counterparty_jurisdiction: cpValidation?.jurisdiction || null,
        counterparty_entity_designation: cpValidation?.entity_designation || null,
        // escalate_intent: this request is headed for the human review queue,
        // so a populated template draft must NOT take the send-ready landing.
        escalate_intent: true,
        description: `Self-service questionnaire (escalated due to existing agreement):\n\n${chatSummary}${cpAddr ? `\n\nCounterparty Address: ${cpAddr}` : ""}${cpValidation?.entity_designation ? `\nEntity Type: ${cpValidation.entity_designation}` : ""}`
      })
    });
    if (data?.request) {
      await apiCall(`/self-service/escalate/${data.request.id}`, { method: "POST" });
      setRequestCreated({ ...data.request, escalated: true });
    } else { alert("Failed to create request"); }
    setEscalating(false);
  };

  // No-template-match fallback, path (a): create a Request WITHOUT a document
  // — the legal team drafts it, so this is the one self-service path that
  // SHOULD land in the assignment queue. Reuses the existing create-request +
  // escalate machinery; the chat transcript rides along as the description.
  const [fallbackDesc, setFallbackDesc] = useState("");
  // Visible error on the without-document card — NO silent failures. #179
  // regression: the server started requiring a description the form never
  // collected, and the card swallowed the 400, so clicking Create did nothing.
  const [fallbackError, setFallbackError] = useState("");
  const createWithoutDocument = async () => {
    // Both required (owner ruling Round 5): counterparty AND a real description.
    // Surface it visibly rather than returning silently (the defect being fixed).
    if (!fallbackCp.trim() || !fallbackDesc.trim()) {
      setFallbackError("Please enter both a counterparty name and a description of what you need.");
      return;
    }
    setFallbackError("");
    setFallbackCreating(true);
    try {
      // without_document → the server requires the description and lands the
      // request directly in the assignment queue (no second escalate call).
      const data = await apiCall("/self-service/create-request", {
        method: "POST",
        body: JSON.stringify({
          title: firstUserText() || "Contract Request",
          counterparty: fallbackCp.trim(),
          without_document: true,
          description: `${fallbackDesc.trim()}\n\n--- Self-Service Chat Summary ---\n\n${chatSummaryText()}`,
          business_unit: form.business_unit || chatBusinessUnit || null,
        })
      });
      if (data?.request) {
        setRequestCreated({ ...data.request, escalated: true });
      } else {
        // apiCall collapses any non-2xx to null (e.g. the server's
        // description_required 400) — surface a clear message on the card.
        setFallbackError("Could not create the request. Please add a description of what you need and try again.");
      }
    } catch (err: any) {
      console.error("[SelfService] Without-document create error:", err);
      setFallbackError("Network error — please try again.");
    }
    setFallbackCreating(false);
  };

  const hasDuplicates = duplicates.length > 0;
  const inp: CSSProperties = { width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box" };

  if (requestCreated) {
    return (
      <div style={{ flex: 1, overflow: "auto", padding: "32px 40px", maxWidth: 700, margin: "0 auto" }}>
        <Card style={{ padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>&#10003;</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.tx, marginBottom: 8 }}>{requestCreated.escalated ? "Request Escalated" : "Request Created"}</div>
          <div style={{ fontSize: 13, color: T.txM, marginBottom: 4 }}>"{requestCreated.title}" with {requestCreated.counterparty}</div>
          <div style={{ fontSize: 12, color: T.txD, marginBottom: 20 }}>{requestCreated.escalated ? "Your request has been sent to the appropriate team for review and assignment." : requestCreated.status === "counterparty_draft_ready" ? "Your draft is ready to send to the counterparty — it has been prepared from the approved template and emailed to you. Send it to the counterparty, then reply \"SENT\" to the email or click Mark as Sent on the request." : requestCreated.versionCreated ? "A draft has been prepared with counterparty details populated. You can view and download it from the Versions tab in the Requests page, or escalate for review." : "Status: Self-Service Draft. You can view this request from the Requests page, or escalate for review."}</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Btn v="primary" onClick={() => { setRequestCreated(null); setMessages([]); setTemplateSelected(null); setForm({ title: "", counterparty: "" }); setCpValidation(null); setDuplicates([]); setDupeChecked(false); setDealDetails(null); }}>Start New</Btn>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Single entry point (owner ruling 2026-07-23, Round 5): the Ask-a-legal-
          question button is removed — the chat box below handles both jobs.
          The subtitle now covers drafting help AND legal questions so the screen
          doesn't contradict itself. */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.brd}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.tx }}>Self-Service Legal Requests</div>
          <div style={{ fontSize: 12, color: T.txM }}>Tell us about the contract you need and we'll find the right template — or ask a legal question and get an answer, right here.</div>
        </div>
      </div>

      {/* Ask-Legal AI answer view: question → grounded facts-only answer with a
          PERSISTENT AI-generated notice → one-click hop to counsel via the
          existing question form (which stays intact below as the fallback). */}
      <Modal open={showAsk} onClose={() => setShowAsk(false)} title="Ask a Legal Question" w={640}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.txD, marginBottom: 6, letterSpacing: ".04em", textTransform: "uppercase" }}>Your Question</label>
          <textarea value={askQuestion} onChange={e => setAskQuestion(e.target.value)} placeholder='Ask about your contracts or requests — e.g. "What is the status of my NDA request?" or "When does our agreement with Acme expire?"' rows={3} style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 14, resize: "vertical", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: askAnswer || askLoading ? 16 : 0 }}>
          <Btn v="secondary" onClick={() => sendToCounsel(askQuestion, null)} disabled={!askQuestion.trim()}>Skip AI — send to counsel</Btn>
          <Btn v="primary" onClick={() => runAskLegal(askQuestion)} disabled={askLoading || !askQuestion.trim()}>{askLoading ? "Checking records..." : "Get AI Answer"}</Btn>
        </div>
        {askLoading && <div style={{ fontSize: 12, color: T.txM, padding: "8px 0" }}>Reading your contracts and requests...</div>}
        {askAnswer && !askLoading && (
          <div style={{ border: `1px solid ${T.brd}`, borderRadius: 8, overflow: "hidden" }}>
            <div style={{ padding: "12px 14px", fontSize: 13, lineHeight: 1.55, color: T.tx, backgroundColor: T.bg3, whiteSpace: "pre-wrap" }}>{renderMarkdown(askAnswer.answer)}</div>
            <div style={{ padding: "8px 14px", fontSize: 11, color: T.txM, backgroundColor: T.ylwS, borderTop: `1px solid ${T.brd}` }}>
              This is an AI-generated response, not legal advice. For advice on your situation, send this to counsel for review.
            </div>
            <div style={{ padding: "10px 14px", display: "flex", justifyContent: "flex-end", gap: 8, borderTop: `1px solid ${T.brd}` }}>
              {askAnswer.interpretive && <span style={{ fontSize: 11, color: T.orn, alignSelf: "center", marginRight: "auto" }}>This question needs a lawyer's review.</span>}
              <Btn v="primary" onClick={() => sendToCounsel(askAnswer.question, askAnswer.answer)}>Send to counsel for review</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={showQuestion} onClose={() => setShowQuestion(false)} title="Ask a Legal Question" w={560}>
        <Inp label="Title" value={qForm.title} onChange={(v: string) => setQForm((p: any) => ({ ...p, title: v }))} required />
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.txD, marginBottom: 6, letterSpacing: ".04em", textTransform: "uppercase" }}>Matter Type<span style={{ color: T.red }}> *</span></label>
          <select value={qForm.matter_type} onChange={e => setQForm((p: any) => ({ ...p, matter_type: e.target.value }))} style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${!qForm.matter_type ? T.red : T.brd}`, backgroundColor: T.bg3, color: qForm.matter_type ? T.tx : T.txD, fontSize: 14, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}>
            <option value="">Select matter type...</option>
            <option value="Client">Client</option>
            <option value="Corporate">Corporate</option>
            <option value="Procurement">Procurement</option>
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.txD, marginBottom: 6, letterSpacing: ".04em", textTransform: "uppercase" }}>Your Question<span style={{ color: T.red }}> *</span></label>
          <textarea value={qForm.description} onChange={e => setQForm((p: any) => ({ ...p, description: e.target.value }))} placeholder="Describe your legal question and any helpful context." rows={5} style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${!qForm.description?.trim() ? T.red : T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 14, resize: "vertical", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4, fontWeight: 700 }}>Business Unit <span style={{ textTransform: "none", fontWeight: 400 }}>(optional)</span></div>
            <select value={qForm.business_unit || ""} onChange={e => setQForm((p: any) => ({ ...p, business_unit: e.target.value }))} style={{ width: "100%", padding: "10px 12px", border: `1px solid ${T.brd}`, borderRadius: 6, fontSize: 13, backgroundColor: T.bg3, color: T.tx, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}><option value="">Select...</option>{ssBusinessUnits.map((bu: any) => <option key={bu.id} value={bu.name}>{bu.name}</option>)}</select>
          </div>
          <Sel label="Priority" value={qForm.priority} onChange={(v: string) => setQForm((p: any) => ({ ...p, priority: v }))} options={["Low", "Normal", "High", "Urgent"]} />
        </div>
        <div style={{ marginTop: 12 }}>
          <Inp label="Needed-by Date" value={qForm.targetDate} onChange={(v: string) => setQForm((p: any) => ({ ...p, targetDate: v }))} type="date" />
        </div>
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4, fontWeight: 700 }}>Attachment <span style={{ textTransform: "none", fontWeight: 400 }}>(optional)</span></div>
          {qFile ? (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", backgroundColor: T.bg3, borderRadius: 6, border: `1px solid ${T.brd}` }}>
              <span style={{ fontSize: 12, color: T.tx }}>📎 {qFile.name}</span>
              <button onClick={() => setQFile(null)} style={{ background: "none", border: "none", color: T.txD, cursor: "pointer", fontFamily: "inherit" }}>✕</button>
            </div>
          ) : (<>
            <input id="portalQFile" type="file" accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.txt,.csv,.xlsx" style={{ display: "none" }} onChange={e => { const file = (e.target as HTMLInputElement).files?.[0]; if (file) setQFile(file); }} />
            <label htmlFor="portalQFile" style={{ display: "inline-block", padding: "8px 14px", fontSize: 12, color: T.acc, backgroundColor: T.accS, border: `1px solid ${T.acc}33`, borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Choose a file</label>
          </>)}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <Btn v="secondary" onClick={() => { setShowQuestion(false); setQFile(null); }}>Cancel</Btn>
          <Btn v="primary" onClick={submitPortalQuestion} disabled={qCreating || !qForm.title?.trim() || !qForm.matter_type || !qForm.description?.trim()}>{qCreating ? "Submitting..." : "Submit Question"}</Btn>
        </div>
      </Modal>

      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px" }}>
        {messages.length === 0 && !loading && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>&#9830;</div>
            {/* Starter prompts derived live from the tenant's GENERIC templates
                (owner ruling: never hardcoded; counterparty-specific templates
                surface via the cascade, not here). Zero generic templates →
                the PR #175 two-option fallback instead of an empty panel. */}
            {starters === null ? (
              <div style={{ fontSize: 13, color: T.txD }}>Loading templates...</div>
            ) : starters.length > 0 ? (
              <>
                <div style={{ fontSize: 14, color: T.txM, marginBottom: 20 }}>Tell us about the contract you need. Try one of these:</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 400, margin: "0 auto" }}>
                  {starters.map((s: any) => (
                    <button key={s.id} onClick={() => sendMessage(s.prompt)} style={{ padding: "10px 16px", fontSize: 13, color: T.acc, backgroundColor: T.accS, border: `1px solid ${T.acc}22`, borderRadius: 8, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>{s.prompt}</button>
                  ))}
                </div>
                {/* Single entry point (Round 5): questions go through the same chat box. */}
                <div style={{ fontSize: 13, color: T.txD, marginTop: 18 }}>…or ask a legal question in the chat box below.</div>
              </>
            ) : (
              <div style={{ maxWidth: 480, margin: "0 auto", textAlign: "left" }}>
                <div style={{ fontSize: 14, color: T.txM, marginBottom: 12, textAlign: "center" }}>No self-service templates are set up yet, but we can still help:</div>
                <Card style={{ padding: 16, border: `1px solid ${T.brd}` }}>
                  <div style={{ marginBottom: 12, padding: "10px 12px", backgroundColor: T.bg3, borderRadius: 6, border: `1px solid ${T.brd}` }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, marginBottom: 6 }}>Create a Request without Document</div>
                    <div style={{ fontSize: 11, color: T.txM, marginBottom: 8 }}>The legal team will draft the agreement for you. It goes to the normal assignment queue.</div>
                    <input value={fallbackCp} onChange={e => setFallbackCp(e.target.value)} placeholder="Counterparty name (required)" style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${!fallbackCp.trim() ? T.red : T.brd}`, backgroundColor: T.bg2, color: T.tx, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box", marginBottom: 8 }} />
                    {/* Required description (owner ruling Round 5): the chat transcript
                        alone is near-empty for a one-line exchange, so ask for real detail. */}
                    <textarea value={fallbackDesc} onChange={e => setFallbackDesc(e.target.value)} rows={3} placeholder="What do you need? (required) — what the agreement is for, its scope and purpose" style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${!fallbackDesc.trim() ? T.red : T.brd}`, backgroundColor: T.bg2, color: T.tx, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box", resize: "vertical", marginBottom: 8 }} />
                    {fallbackError && <div style={{ fontSize: 11, color: T.red, marginBottom: 8 }}>{fallbackError}</div>}
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <Btn v="primary" sz="sm" disabled={!fallbackCp.trim() || !fallbackDesc.trim() || fallbackCreating} onClick={() => createWithoutDocument()}>{fallbackCreating ? "Creating..." : "Create Request"}</Btn>
                    </div>
                  </div>
                  <div style={{ padding: "10px 12px", backgroundColor: T.bg3, borderRadius: 6, border: `1px solid ${T.brd}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, marginBottom: 2 }}>Ask a Legal Question</div>
                      <div style={{ fontSize: 11, color: T.txM }}>Send a question for the legal team to answer.</div>
                    </div>
                    <Btn v="secondary" sz="sm" onClick={() => { setAskQuestion(""); setAskAnswer(null); setShowAsk(true); }}>Ask a Question</Btn>
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}

        {messages.map((m: any, i: number) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 12 }}>
            {m.aiAnswer ? (
              /* Ask-Legal answer inside the chat: persistent AI notice + counsel hop */
              <div style={{ maxWidth: "75%", borderRadius: 12, border: `1px solid ${T.brd}`, overflow: "hidden" }}>
                <div style={{ padding: "10px 14px", fontSize: 13, lineHeight: 1.5, color: T.tx, backgroundColor: T.bg3, whiteSpace: "pre-wrap" }}>{renderMarkdown(m.text)}</div>
                <div style={{ padding: "6px 14px", fontSize: 11, color: T.txM, backgroundColor: T.ylwS, borderTop: `1px solid ${T.brd}` }}>
                  This is an AI-generated response, not legal advice. For advice on your situation, send this to counsel for review.
                </div>
                <div style={{ padding: "8px 14px", display: "flex", justifyContent: "flex-end", borderTop: `1px solid ${T.brd}`, backgroundColor: T.bg3 }}>
                  <Btn v="secondary" sz="sm" onClick={() => sendToCounsel(m.question || m.text, m.text)}>Send to counsel for review</Btn>
                </div>
              </div>
            ) : (
              <div style={{ maxWidth: "75%", padding: "10px 14px", borderRadius: 12, fontSize: 13, lineHeight: 1.5, color: m.role === "user" ? "#fff" : T.tx, backgroundColor: m.role === "user" ? T.acc : T.bg3, border: m.role === "user" ? "none" : `1px solid ${T.brd}`, whiteSpace: "pre-wrap" }}>{m.text}</div>
            )}
          </div>
        ))}

        {/* No-template-match: two live paths carrying the chat context forward
            (owner ruling: never dead-end the user). The user chooses — no
            automatic guess. */}
        {noTemplateOffer && !loading && !templateSelected && (
          <Card style={{ marginTop: 8, marginBottom: 12, padding: 16, border: `1px solid ${T.brd}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 4 }}>How would you like to continue?</div>
            <div style={{ fontSize: 12, color: T.txM, marginBottom: 12 }}>Your chat so far will be carried into whichever you pick.</div>
            <div style={{ marginBottom: 12, padding: "10px 12px", backgroundColor: T.bg3, borderRadius: 6, border: `1px solid ${T.brd}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, marginBottom: 6 }}>Create a Request without Document</div>
              <div style={{ fontSize: 11, color: T.txM, marginBottom: 8 }}>The legal team will draft the agreement for you. It goes to the normal assignment queue.</div>
              <input value={fallbackCp} onChange={e => setFallbackCp(e.target.value)} placeholder="Counterparty name (required)" style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${!fallbackCp.trim() ? T.red : T.brd}`, backgroundColor: T.bg2, color: T.tx, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box", marginBottom: 8 }} />
              {/* #179 regression fix: this mid-chat card was missing the required
                  description field, so its Create button silently no-op'd. */}
              <textarea value={fallbackDesc} onChange={e => setFallbackDesc(e.target.value)} rows={3} placeholder="What do you need? (required) — what the agreement is for, its scope and purpose" style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${!fallbackDesc.trim() ? T.red : T.brd}`, backgroundColor: T.bg2, color: T.tx, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box", resize: "vertical", marginBottom: 8 }} />
              {fallbackError && <div style={{ fontSize: 11, color: T.red, marginBottom: 8 }}>{fallbackError}</div>}
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Btn v="primary" sz="sm" disabled={!fallbackCp.trim() || !fallbackDesc.trim() || fallbackCreating} onClick={() => createWithoutDocument()}>{fallbackCreating ? "Creating..." : "Create Request"}</Btn>
              </div>
            </div>
            <div style={{ padding: "10px 12px", backgroundColor: T.bg3, borderRadius: 6, border: `1px solid ${T.brd}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, marginBottom: 2 }}>Ask a Legal Question</div>
                <div style={{ fontSize: 11, color: T.txM }}>Send this as a question for the legal team to answer.</div>
              </div>
              <Btn v="secondary" sz="sm" onClick={() => {
                setQForm((p: any) => ({ ...p, title: firstUserText() || "Legal question", description: chatSummaryText() }));
                setShowQuestion(true);
              }}>Ask a Question</Btn>
            </div>
          </Card>
        )}

        {loading && (
          <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 12 }}>
            <div style={{ padding: "10px 14px", borderRadius: 12, fontSize: 13, color: T.txM, backgroundColor: T.bg3, border: `1px solid ${T.brd}` }}>{loadingStatus}</div>
          </div>
        )}

        {/* Suggested answer buttons */}
        {suggestions.length > 0 && !loading && !templateSelected && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12, justifyContent: "flex-end" }}>
            {suggestions.map((s: any, i: number) => {
              const isOther = s.toLowerCase().includes("other") || s.toLowerCase().includes("something else") || s.toLowerCase().includes("none of");
              return isOther ? (
                <button key={i} onClick={() => setShowOtherInput(true)} style={{ padding: "8px 14px", fontSize: 12, color: T.txM, backgroundColor: "transparent", border: `1px dashed ${T.brd}`, borderRadius: 16, cursor: "pointer", fontFamily: "inherit" }}>{s}</button>
              ) : (
                <button key={i} onClick={() => sendMessage(s)} style={{ padding: "8px 14px", fontSize: 12, color: T.acc, backgroundColor: T.accS, border: `1px solid ${T.acc}22`, borderRadius: 16, cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}>{s}</button>
              );
            })}
          </div>
        )}
        {showOtherInput && !loading && (
          <div style={{ display: "flex", gap: 6, marginBottom: 12, justifyContent: "flex-end", alignItems: "center" }}>
            <VoiceInput onTranscript={(text: string) => setInput(text)} disabled={false} />
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && sendMessage()} placeholder="Type your answer..." autoFocus style={{ padding: "8px 12px", borderRadius: 16, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: "inherit", minWidth: 200 }} />
            <button onClick={() => sendMessage()} disabled={!input.trim()} style={{ padding: "8px 14px", fontSize: 12, color: "#fff", backgroundColor: T.acc, border: "none", borderRadius: 16, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, opacity: input.trim() ? 1 : 0.5 }}>Send</button>
            <button onClick={() => { setShowOtherInput(false); setInput(""); }} style={{ padding: "8px 10px", fontSize: 12, color: T.txD, backgroundColor: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          </div>
        )}

        {templateSelected && !requestCreated && (
          <Card style={{ marginTop: 16, padding: 16, border: `2px solid ${T.grn}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Badge c={T.grn} b={T.grnS}>Template Found</Badge>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.tx }}>{templateSelected.name}</span>
            </div>
            <div style={{ fontSize: 12, color: T.txM, marginBottom: 16 }}>Type: {templateSelected.agreement_type}</div>

            {/* Counterparty input */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.txD, marginBottom: 4 }}>Counterparty Name *</label>
              <input value={form.counterparty} onChange={e => setForm((p: any) => ({ ...p, counterparty: e.target.value }))} placeholder="Enter the other party's name..." style={inp} />
              {cpValidating && <div style={{ fontSize: 10, color: T.acc, marginTop: 4 }}>Validating counterparty...</div>}

              {/* AI-validated counterparty info */}
              {cpValidation && !cpValidating && form.counterparty.length >= 3 && (
                <div style={{ marginTop: 6, padding: "8px 12px", backgroundColor: cpValidation.confidence === "high" ? "rgba(43,122,62,.06)" : "rgba(208,164,64,.06)", border: `1px solid ${cpValidation.confidence === "high" ? "rgba(43,122,62,.15)" : "rgba(208,164,64,.15)"}`, borderRadius: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <Badge c={cpValidation.confidence === "high" ? T.grn : T.ylw} b={cpValidation.confidence === "high" ? T.grnS : T.ylwS}>{cpValidation.confidence} confidence</Badge>
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.tx }}>{cpValidation.legal_name}</span>
                    {cpValidation.legal_name !== form.counterparty && (
                      <button onClick={() => setForm((p: any) => ({ ...p, counterparty: cpValidation.legal_name }))} style={{ fontSize: 10, color: T.acc, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Use this name</button>
                    )}
                  </div>
                  {cpValidation.entity_designation && <div style={{ fontSize: 11, color: T.tx, marginBottom: 2 }}>{cpValidation.entity_designation}</div>}
                  {cpValidation.address && <div style={{ fontSize: 11, color: T.txM }}>{cpValidation.address}</div>}
                  {cpValidation.notes && <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>{cpValidation.notes}</div>}
                </div>
              )}
            </div>

            {/* Duplicate warning */}
            {hasDuplicates && dupeChecked && (
              <div style={{ marginBottom: 12, padding: "12px 14px", backgroundColor: "rgba(208,64,64,.06)", border: "1px solid rgba(208,64,64,.15)", borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.red, marginBottom: 6 }}>Existing agreement(s) found</div>
                <div style={{ fontSize: 11, color: T.txM, marginBottom: 8 }}>A similar agreement with this counterparty already exists. Self-service is not available for this request. Please escalate to the appropriate team for assistance.</div>
                {duplicates.map((d: any, i: number) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 11, borderBottom: i < duplicates.length - 1 ? `1px solid ${T.brdSubtle}` : "none" }}>
                    <Badge c={d.type === "request" ? T.orn : T.blu} b={d.type === "request" ? T.ornS : T.bluS}>{d.type === "request" ? "Pending Request" : "Executed"}</Badge>
                    <span style={{ color: T.tx, fontWeight: 500 }}>{d.title}</span>
                    <span style={{ color: T.txD }}>({d.counterparty})</span>
                    {d.status && d.status !== "executed" && <span style={{ color: T.txD }}>- {d.status.replace(/_/g, " ")}</span>}
                  </div>
                ))}
                <div style={{ marginTop: 10 }}>
                  <Btn v="primary" disabled={escalating} onClick={() => { escalateToQueue(); }} style={{ backgroundColor: T.orn, borderColor: T.orn }}>
                    {escalating ? "Escalating..." : "Escalate to Review Queue"}
                  </Btn>
                </div>
              </div>
            )}

            {/* Form fields after template match */}
            {!hasDuplicates && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.txD, marginBottom: 4 }}>Description <span style={{ fontWeight: 400, color: T.txM }}>(optional — auto-generated from chat if blank)</span></label>
                  <textarea value={form.description || ""} onChange={e => setForm((p: any) => ({ ...p, description: e.target.value }))} placeholder="Describe the scope and purpose of this agreement..." rows={3} style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box", resize: "vertical" }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.txD, marginBottom: 4 }}>Business Unit</label>
                  <select value={form.business_unit || ""} onChange={e => setForm((p: any) => ({ ...p, business_unit: e.target.value }))} style={{ ...inp, cursor: "pointer" }}><option value="">Select...</option>{ssBusinessUnits.map(bu => <option key={bu.id} value={bu.name}>{bu.name}</option>)}</select>
                </div>
                <Btn v="primary" disabled={!form.counterparty || creating || cpValidating} onClick={() => { createRequest(); }}>{creating ? "Preparing draft..." : "Create Request"}</Btn>
              </>
            )}
          </Card>
        )}

        <div ref={messagesEndRef} />
      </div>

      {(suggestions.length === 0 || showOtherInput || messages.length === 0) && !templateSelected && (
        <div style={{ padding: "12px 24px", borderTop: `1px solid ${T.brd}`, display: "flex", gap: 8, alignItems: "center" }}>
          <VoiceInput onTranscript={(text: string) => setInput(text)} disabled={loading} />
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && sendMessage()} placeholder={suggestions.length > 0 ? "Type your answer..." : "Describe what you need..."} disabled={loading} style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13, outline: "none", fontFamily: "inherit" }} />
          <Btn v="primary" disabled={!input.trim() || loading} onClick={() => sendMessage()}>Send</Btn>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════ SELF-SERVICE TEMPLATE MANAGER (ADMIN) ═══════════════════ */
